import React, { useState } from "react"
import { Table } from "react-bootstrap"
import _ from "lodash"

import MultiSelect from "@/components/input/MultiSelect"
import { fieldLabel } from "@/onboarding/features/product/models/fieldLabels"

import FieldPermissionRow from "@/onboarding/features/bidding/components/FieldPermissionRow"
import PermissionToggle from "@/onboarding/features/bidding/components/PermissionToggle"
import ReviewChanges from "@/onboarding/features/bidding/components/ReviewChanges"
import SelectedToursPermissionStatus from "@/onboarding/features/bidding/components/SelectedToursPermissionStatus"
import { EDITABLE_OPTIONS, REQUIRED_OPTIONS } from "@/onboarding/features/bidding/flagOptions"
import useFieldStates from "@/onboarding/features/bidding/hooks/useFieldStates"
import {
  CatalogueField,
  FieldSetting,
  FieldSettingWithCurrentState,
  PermissionFlag,
  TourFieldPermissions,
} from "@/onboarding/features/bidding/types"

const NO_ROW_CAN_SET_REQUIRED_REASON =
  "None of the picked fields can be set to Required or Optional"

type Props = {
  name: string
  label: string
  fields: CatalogueField[]
  picked: FieldSetting[]
  tours: { input: string; permissions: TourFieldPermissions[] }
}

const groupedOptions = (fields: CatalogueField[]) =>
  _.map(_.groupBy(fields, "section"), (sectionFields, section) => ({
    label: _.startCase(section),
    items: sectionFields.map((field) => ({ label: fieldLabel(field.name), value: field.name })),
  }))

const submittedValue = (rows: FieldSetting[]) =>
  JSON.stringify(
    Object.fromEntries(
      rows
        .filter((row) => row.writeable !== undefined || row.required !== undefined)
        .map(({ name, ...flags }) => [name, flags])
    )
  )

const sharedPermission = (rows: FieldSetting[], flag: PermissionFlag): boolean | undefined => {
  const values = _.uniq(rows.map((row) => row[flag]))

  if (values.length !== 1) {
    return undefined
  }

  return values[0]
}

const FieldPermissions = ({ name, label, fields, picked, tours }: Props): JSX.Element => {
  const [rows, setRows] = useState<FieldSetting[]>(picked)
  const catalogue = _.keyBy(fields, "name")
  const { selectedTours, displayRows } = useFieldStates(rows, tours)

  const choose = (names: string[]) => {
    setRows((current) =>
      names.map((fieldName) => current.find((row) => row.name === fieldName) ?? { name: fieldName })
    )
  }

  const update = (fieldName: string, change: Partial<FieldSetting>) => {
    setRows((current) =>
      current.map((row) => (row.name === fieldName ? { ...row, ...change } : row))
    )
  }

  const setWriteable = (fieldName: string, writeable: boolean | undefined) => {
    if (writeable === false) {
      update(fieldName, { writeable, required: undefined })
    } else {
      update(fieldName, { writeable })
    }
  }

  const remove = (fieldName: string) => {
    setRows((current) => current.filter((row) => row.name !== fieldName))
  }

  const status = (row: FieldSettingWithCurrentState, permissionFlag: PermissionFlag) => (
    <SelectedToursPermissionStatus
      selectedTours={selectedTours}
      fieldName={row.name}
      permissionFlag={permissionFlag}
      pickedPermissionValue={row.pickedSetting[permissionFlag]}
      onReset={() => update(row.name, { [permissionFlag]: undefined })}
    />
  )

  const canSetRequired = (row: FieldSetting) =>
    catalogue[row.name].requiredConfigurable && row.writeable !== false

  const rowsThatCanSetRequired = displayRows.filter(canSetRequired)

  const setAllWriteable = (writeable: boolean | undefined) => {
    rows.forEach((row) => setWriteable(row.name, writeable))
  }

  const setRequiredWherePossible = (required: boolean | undefined) => {
    rowsThatCanSetRequired.forEach((row) => update(row.name, { required }))
  }

  return (
    <div className="d-flex flex-column gap-3">
      <MultiSelect
        name={undefined}
        inputId="field-permissions-picker"
        label={label}
        options={groupedOptions(fields)}
        optionGroupLabel="label"
        optionGroupChildren="items"
        value={rows.map((row) => row.name)}
        onChange={(event) => choose(event.value)}
        display="chip"
        placeholder="Search fields"
      />
      {rows.length > 0 && (
        <Table size="sm" className="align-middle">
          <thead>
            <tr>
              <th scope="col">Field</th>
              <th scope="col">Editable?</th>
              <th scope="col">Required?</th>
              <th scope="col">
                <span className="visually-hidden">Remove</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="table-light">
              <th scope="row">All picked fields</th>
              <td>
                <PermissionToggle
                  subject="All picked fields"
                  options={EDITABLE_OPTIONS}
                  selected={sharedPermission(displayRows, PermissionFlag.WRITEABLE)}
                  disabledReason={undefined}
                  onChange={setAllWriteable}
                />
              </td>
              <td>
                <PermissionToggle
                  subject="All picked fields"
                  options={REQUIRED_OPTIONS}
                  selected={sharedPermission(rowsThatCanSetRequired, PermissionFlag.REQUIRED)}
                  disabledReason={
                    rowsThatCanSetRequired.length === 0 ? NO_ROW_CAN_SET_REQUIRED_REASON : undefined
                  }
                  onChange={setRequiredWherePossible}
                />
              </td>
              <td />
            </tr>
            {displayRows.map((row) => (
              <FieldPermissionRow
                key={row.name}
                row={row}
                field={catalogue[row.name]}
                canSetRequired={canSetRequired(row)}
                writeableStatus={status(row, PermissionFlag.WRITEABLE)}
                requiredStatus={status(row, PermissionFlag.REQUIRED)}
                onWriteableChange={(writeable) => setWriteable(row.name, writeable)}
                onRequiredChange={(required) => update(row.name, { required })}
                onRemove={() => remove(row.name)}
              />
            ))}
          </tbody>
        </Table>
      )}
      <ReviewChanges rows={displayRows} tours={selectedTours} />
      <input type="hidden" name={name} value={submittedValue(rows)} />
    </div>
  )
}

export default FieldPermissions
